/**
 * Browser-only helpers for the image converter. They use FileReader and Image, so they
 * run from event handlers, never during render. Nothing here makes a network request:
 * a file is read from the user's disk into memory and drawn from a data URI.
 */

import { extensionFor, sniffMime } from "@/lib/tool-kit/latex-dev/image-data";
import { decodeBase64Url } from "@/lib/tool-kit/latex-dev/jwt";

export interface LoadedImage {
  name: string;
  mime: string;
  bytes: number;
  base64: string;
  /** The SVG source for an SVG file, else an empty string. */
  svgText: string;
  width: number;
  height: number;
}

export type LoadError = "tooBig" | "notImage" | "readFailed";

export type LoadResult =
  | { ok: true; image: LoadedImage }
  | { ok: false; error: LoadError; mime: string; bytes: number };

/** The largest file the page will take: bigger files make the page, not the file, the problem. */
export const MAX_BYTES = 10 * 1024 * 1024;

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  ico: "image/x-icon",
  avif: "image/avif",
  tif: "image/tiff",
  tiff: "image/tiff",
};

function readerPromise<T extends string>(
  read: (reader: FileReader) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as T);
    reader.onerror = () => reject(reader.error);
    read(reader);
  });
}

/** Natural size of an image drawn from a data URI, or null if the browser cannot draw it. */
export function measureImage(
  dataUri: string
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = dataUri;
  });
}

function mimeFor(file: File, base64: string): string {
  if (file.type !== "") {
    return file.type;
  }
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const bytes = decodeBase64Url(base64.slice(0, 2000));
  return (
    MIME_BY_EXTENSION[extension] ?? (bytes ? sniffMime(bytes) : null) ?? ""
  );
}

/** Read a file into a Base64 string, checking its size and type first. */
export async function loadImageFile(file: File): Promise<LoadResult> {
  if (file.size > MAX_BYTES) {
    return { ok: false, error: "tooBig", mime: file.type, bytes: file.size };
  }
  try {
    const url = await readerPromise<string>((r) => r.readAsDataURL(file));
    const base64 = url.slice(url.indexOf(",") + 1);
    const mime = mimeFor(file, base64);
    if (!mime.startsWith("image/")) {
      return {
        ok: false,
        error: "notImage",
        mime: mime || file.type,
        bytes: file.size,
      };
    }
    const svgText =
      mime === "image/svg+xml"
        ? await readerPromise<string>((r) => r.readAsText(file))
        : "";
    const size = await measureImage(`data:${mime};base64,${base64}`);
    return {
      ok: true,
      image: {
        name: file.name,
        mime,
        bytes: file.size,
        base64,
        svgText,
        width: size?.width ?? 0,
        height: size?.height ?? 0,
      },
    };
  } catch {
    return {
      ok: false,
      error: "readFailed",
      mime: file.type,
      bytes: file.size,
    };
  }
}

/** A file name for a decoded image: image.png, image.jpg … */
export function fileNameFor(mime: string): string {
  return `image.${extensionFor(mime)}`;
}
